/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import i18n from 'i18next';
import type { UseTranslationResponse } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { CORE_NAMESPACE, coreTranslations } from '@opengeoweb/core';
import {
  FORM_FIELDS_NAMESPACE,
  formFieldsTranslations,
} from '@opengeoweb/form-fields';
import {
  safelyInitI18nForTestsAndStories,
  SHARED_NAMESPACE,
  sharedTranslations,
} from '@opengeoweb/shared';
import { SA_NAMESPACE, saTranslations } from '@opengeoweb/sigmet-airmet';
import { WEBMAP_NAMESPACE, webmapTranslations } from '@opengeoweb/webmap';
import {
  WEBMAP_REACT_NAMESPACE,
  webmapReactTranslations,
} from '@opengeoweb/webmap-react';
import warningsTranslations from '../../../locales/warnings.json';

export const WARN_NAMESPACE = 'warn';

export const initWarnI18n = (): void => {
  safelyInitI18nForTestsAndStories(i18n, WARN_NAMESPACE, [
    { ns: WARN_NAMESPACE, translations: warningsTranslations },
    { ns: SHARED_NAMESPACE, translations: sharedTranslations },
    { ns: WEBMAP_NAMESPACE, translations: webmapTranslations },
    { ns: WEBMAP_REACT_NAMESPACE, translations: webmapReactTranslations },
    { ns: CORE_NAMESPACE, translations: coreTranslations },
    { ns: FORM_FIELDS_NAMESPACE, translations: formFieldsTranslations },
    { ns: SA_NAMESPACE, translations: saTranslations },
  ]);
};

const ns = [
  WARN_NAMESPACE,
  FORM_FIELDS_NAMESPACE,
  SHARED_NAMESPACE,
  SA_NAMESPACE,
  WEBMAP_REACT_NAMESPACE,
];

export const translateInTestsAndStories = (
  key: string,
  params: Record<string, string | number> | undefined = undefined,
): string => i18n.t(key, { ns, ...params });

export const useWarningsTranslation = (): UseTranslationResponse<
  typeof WARN_NAMESPACE,
  typeof i18n
> => useTranslation(ns);

export { i18n };
